import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Slider,
  Switch,
  Button,
  Typography,
  Space,
  Tag,
  Tooltip,
  Spin,
  message
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVolumeHigh,
  faBolt,
  faComment,
  faPhone,
  faFloppyDisk,
  faCircleCheck,
  faCircleInfo,
  faFileLines,
  faCirclePlay,
  faWrench,
  faArrowRotateLeft,
  faSliders
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import SettingsService from '../../../services/SettingsService';
import VoiceCallModal from './VoiceCallModal';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly & Warm' },
  { value: 'professional', label: 'Professional & Formal' },
  { value: 'empathetic', label: 'Empathetic & Caring' },
  { value: 'concise', label: 'Concise & Direct' },
  { value: 'casual', label: 'Casual & Conversational' }
];

const VoiceSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [voices, setVoices] = useState([]);
  const [options, setOptions] = useState({});
  const [variables, setVariables] = useState([]);
  const [tools, setTools] = useState([]);
  const [settings, setSettings] = useState(null);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [callModalVisible, setCallModalVisible] = useState(false);
  const promptRef = useRef(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, voicesRes, varsRes, toolsRes] = await Promise.all([
        SettingsService.getVoiceCallSettings(),
        SettingsService.getVoiceOptions(),
        SettingsService.getPromptVariables(),
        SettingsService.getVoiceTools()
      ]);

      if (voicesRes.success) {
        setVoices(voicesRes.data || []);
        setOptions(voicesRes.options || {});
        setConfigured(voicesRes.configured !== false);
      }
      if (varsRes.success) setVariables(varsRes.data || []);
      if (toolsRes.success) setTools(toolsRes.data || []);
      if (settingsRes.success) {
        setSettings(settingsRes.data);
        setDefaultPrompt(settingsRes.defaultSystemPrompt || '');
      }
    } catch (err) {
      console.error('Error loading voice settings:', err);
      message.error('Failed to load voice settings');
    } finally {
      setLoading(false);
    }
  };

  const update = (path, value) => {
    setSettings((prev) => {
      const next = { ...prev };
      if (path.includes('.')) {
        const [parent, child] = path.split('.');
        next[parent] = { ...(next[parent] || {}), [child]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };

  const insertVariable = (key) => {
    const token = `{{${key}}}`;
    const el = promptRef.current?.resizableTextArea?.textArea;
    // When the box is showing the default (systemPrompt empty), materialize it
    // so inserting a variable doesn't wipe the default text.
    const current = settings.systemPrompt || defaultPrompt || '';

    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + token + current.slice(end);
      update('systemPrompt', next);
      // Restore caret just after the inserted token.
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    } else {
      update('systemPrompt', current + token);
    }
  };

  // Clear any customization so the prompt tracks the live default again.
  const resetPromptToDefault = () => {
    update('systemPrompt', '');
    message.success('AI prompt reset to the default');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await SettingsService.updateVoiceCallSettings(settings);
      if (res.success) {
        setSettings(res.data);
        if (res.defaultSystemPrompt !== undefined) {
          setDefaultPrompt(res.defaultSystemPrompt || '');
        }
        if (res.syncError) {
          message.warning(`Settings saved, but Vapi sync failed: ${res.syncError}`);
        } else {
          message.success('Voice settings saved');
        }
      } else {
        message.error(res.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving voice settings:', err);
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const sectionHeader = (icon, title, subtitle) => (
    <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: COLORS.$mainBlack,
          fontSize: 18,
          flexShrink: 0
        }}
      >
        {icon}
      </div>
      <div>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>{subtitle}</Text>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', paddingBottom: 80 }}>
      {!configured && (
        <Card
          style={{ marginBottom: 24, borderColor: '#faad14', background: '#fffbe6' }}
          bodyStyle={{ padding: 16 }}
        >
          <Space>
            <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#faad14' }} />
            <Text>
              Vapi is not fully configured. Set <Text code>VAPI_API_KEY</Text> and{' '}
              <Text code>VAPI_PHONE_NUMBER_ID</Text> on the server to enable live calls.
              You can still configure and save settings.
            </Text>
          </Space>
        </Card>
      )}

      {/* Voice Selection */}
      <Card style={{ marginBottom: 24 }}>
        {sectionHeader(<FontAwesomeIcon icon={faVolumeHigh} />, 'Voice Selection', 'Vapi Voices V2. Pick the voice your AI receptionist uses')}
        <Row gutter={[16, 16]}>
          {voices.map((voice) => {
            const selected = settings.voiceId === voice.id;
            return (
              <Col xs={12} sm={8} md={6} key={voice.id}>
                <div
                  onClick={() => update('voiceId', voice.id)}
                  style={{
                    position: 'relative',
                    border: `2px solid ${selected ? COLORS.$mainBlack : '#f0f0f0'}`,
                    background: selected ? '#f5f5f5' : '#fafafa',
                    borderRadius: 12,
                    padding: '20px 12px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {selected && (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        color: COLORS.$mainBlack,
                        fontSize: 18
                      }}
                    />
                  )}
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: voice.color || COLORS.$mainBlack,
                      margin: '0 auto 12px'
                    }}
                  />
                  <div style={{ fontWeight: 600 }}>{voice.name}</div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    {voice.accent} {voice.gender === 'male' ? 'male' : 'female'}
                  </Text>
                  {voice.description && (
                    <Text type="secondary" style={{ fontSize: 11 }}>{voice.description}</Text>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
        {voices.length > 0 && !voices.some((v) => v.id === settings.voiceId) && (
          <Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
            The saved voice is no longer offered by Vapi. Calls use {voices[0].name} until you pick a voice and save.
          </Text>
        )}
        {options.languages?.length > 0 && (
          <div style={{ marginTop: 20, maxWidth: 360 }}>
            <Text strong>Language</Text>
            <Select
              value={settings.voiceLanguage}
              onChange={(v) => update('voiceLanguage', v)}
              options={options.languages}
              style={{ width: '100%', marginTop: 8 }}
            />
          </div>
        )}
      </Card>

      {/* Personality & Behavior */}
      <Card style={{ marginBottom: 24 }}>
        {sectionHeader(<FontAwesomeIcon icon={faBolt} />, 'Personality & Behavior', 'Customize how your AI receptionist communicates')}

        <div style={{ marginBottom: 20 }}>
          <Text strong>Receptionist Name (optional)</Text>
          <Input
            placeholder="e.g. Sophie, Ava, Jordan"
            value={settings.receptionistName}
            onChange={(e) => update('receptionistName', e.target.value)}
            style={{ marginTop: 8 }}
            maxLength={40}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
            The name your AI introduces itself with on calls. This is also available in the prompt
            as <Text code>{'{{receptionistName}}'}</Text>.
          </Text>
        </div>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Text strong>Conversation Tone</Text>
            <Select
              value={settings.conversationTone}
              onChange={(v) => update('conversationTone', v)}
              style={{ width: '100%', marginTop: 8 }}
            >
              {TONE_OPTIONS.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Speaking Speed</Text>
            <Slider
              min={0.5}
              max={2.0}
              step={0.1}
              value={settings.speakingSpeed}
              onChange={(v) => update('speakingSpeed', v)}
              marks={{ 0.5: 'Slow', 1: 'Normal', 2: 'Fast' }}
              style={{ marginTop: 8 }}
            />
          </Col>
        </Row>
      </Card>

      {/* AI model & call behavior (Vapi assistant settings) */}
      <Card style={{ marginBottom: 24 }}>
        {sectionHeader(<FontAwesomeIcon icon={faSliders} />, 'AI Model & Call Behavior', 'How the AI thinks, takes turns and handles the call')}

        <Row gutter={[24, 20]}>
          <Col xs={24} sm={12}>
            <Text strong>AI Model</Text>
            <Select
              value={settings.aiModel}
              onChange={(v) => update('aiModel', v)}
              options={options.models || []}
              style={{ width: '100%', marginTop: 8 }}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>When the call connects</Text>
            <Select
              value={settings.firstMessageMode}
              onChange={(v) => update('firstMessageMode', v)}
              options={options.firstMessageModes || []}
              style={{ width: '100%', marginTop: 8 }}
            />
          </Col>

          <Col xs={24} sm={12}>
            <Text strong>Creativity</Text>
            <Slider
              min={0}
              max={1}
              step={0.1}
              value={settings.temperature}
              onChange={(v) => update('temperature', v)}
              marks={{ 0: 'Precise', 0.5: 'Default', 1: 'Creative' }}
              style={{ marginTop: 8 }}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Max reply length (tokens)</Text>
            <Slider
              min={100}
              max={600}
              step={25}
              value={settings.maxTokens}
              onChange={(v) => update('maxTokens', v)}
              marks={{ 100: 'Short', 250: 'Default', 600: 'Long' }}
              style={{ marginTop: 8 }}
            />
          </Col>

          <Col xs={24} sm={12}>
            <Text strong>Response delay (seconds)</Text>
            <Slider
              min={0}
              max={2}
              step={0.1}
              value={settings.responseDelaySeconds}
              onChange={(v) => update('responseDelaySeconds', v)}
              marks={{ 0: '0', 0.4: 'Default', 2: '2s' }}
              style={{ marginTop: 8 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Wait this long after the member stops talking. Raise it if the AI cuts people off.
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Words needed to interrupt the AI</Text>
            <Slider
              min={0}
              max={5}
              step={1}
              value={settings.interruptionWords}
              onChange={(v) => update('interruptionWords', v)}
              marks={{ 0: 'Any sound', 2: '2', 5: '5' }}
              style={{ marginTop: 8 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Raise it if background noise or &ldquo;mm-hmm&rdquo; keeps stopping the AI mid-sentence.
            </Text>
          </Col>

          <Col xs={24} sm={12}>
            <Text strong>Max call length (minutes)</Text>
            <Slider
              min={1}
              max={30}
              step={1}
              value={settings.maxCallMinutes}
              onChange={(v) => update('maxCallMinutes', v)}
              marks={{ 1: '1', 10: '10', 30: '30' }}
              style={{ marginTop: 8 }}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>Background sound</Text>
            <Select
              value={settings.backgroundSound}
              onChange={(v) => update('backgroundSound', v)}
              options={options.backgroundSounds || []}
              style={{ width: '100%', marginTop: 8 }}
            />
          </Col>

          <Col xs={24}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Switch
                checked={settings.voicemailDetection === true}
                onChange={(v) => update('voicemailDetection', v)}
              />
              <Text strong>Detect voicemail</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              When a voicemail answers, play the message below (or hang up if it is blank).
            </Text>
            {settings.voicemailDetection === true && (
              <TextArea
                rows={2}
                maxLength={300}
                showCount
                placeholder='e.g. "Hi {{firstName}}, this is the Black Heart Association. Please call us back at this number."'
                value={settings.voicemailMessage}
                onChange={(e) => update('voicemailMessage', e.target.value)}
                style={{ marginTop: 8 }}
              />
            )}
          </Col>
          <Col xs={24}>
            <Text strong>Goodbye message (optional)</Text>
            <Input
              maxLength={200}
              placeholder='e.g. "Thank you, take care. Goodbye!"'
              value={settings.endCallMessage}
              onChange={(e) => update('endCallMessage', e.target.value)}
              style={{ marginTop: 8 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Said when the AI hangs up. Leave blank to end without a fixed line.
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Custom Greeting */}
      <Card style={{ marginBottom: 24 }}>
        {sectionHeader(<FontAwesomeIcon icon={faComment} />, 'Custom Greeting', 'Personalize how your AI answers calls (optional)')}
        <Text strong>Opening Message</Text>
        <TextArea
          rows={3}
          maxLength={500}
          showCount
          placeholder='e.g. "Good morning! Thank you for calling the Black Heart Association. How may I help you today?"'
          value={settings.openingMessage}
          onChange={(e) => update('openingMessage', e.target.value)}
          style={{ marginTop: 8 }}
        />
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          Leave blank to use the default greeting. You can use variables like{' '}
          <Text code>{'{{firstName}}'}</Text>.
        </Text>
      </Card>

      {/* System Prompt + Variables */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          {sectionHeader(<FontAwesomeIcon icon={faFileLines} />, 'AI Prompt', 'The core instructions that control how the AI behaves on calls')}
          <Space>
            <Tag
              style={{ margin: 0, background: (settings.systemPrompt || '').trim() ? '#fff7e6' : '#f6ffed', borderColor: (settings.systemPrompt || '').trim() ? '#ffd591' : '#b7eb8f', color: COLORS.$mainBlack }}
            >
              {(settings.systemPrompt || '').trim() ? 'Custom prompt' : 'Using default'}
            </Tag>
            <Tooltip title="Replace the prompt with the current default. The default stays up to date automatically.">
              <Button
                size="small"
                icon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
                disabled={!(settings.systemPrompt || '').trim()}
                onClick={resetPromptToDefault}
              >
                Reset to default
              </Button>
            </Tooltip>
          </Space>
        </div>

        <div style={{ marginBottom: 12, marginTop: 12 }}>
          <Text strong>Available Variables</Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            Click a variable to insert it at the cursor. Values come from the patient/member record at call time.
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {variables.map((v) => (
              <Tooltip key={v.key} title={v.description}>
                <Tag
                  style={{ cursor: 'pointer', margin: 0, background: '#f5f5f5', borderColor: '#d9d9d9', color: COLORS.$mainBlack }}
                  onClick={() => insertVariable(v.key)}
                >
                  {`{{${v.key}}}`}
                </Tag>
              </Tooltip>
            ))}
          </div>
        </div>

        <TextArea
          ref={promptRef}
          rows={14}
          placeholder="Write the system prompt that guides the AI. Include gating/qualifying questions here if you want them asked during the call."
          value={settings.systemPrompt || defaultPrompt}
          onChange={(e) => update('systemPrompt', e.target.value)}
          style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 13 }}
        />
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          {(settings.systemPrompt || '').trim()
            ? 'This is a custom prompt. Use “Reset to default” to go back to the standard prompt, which updates automatically.'
            : 'Showing the standard default prompt. Editing it here creates a custom prompt; otherwise it stays in sync with future updates automatically.'}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          Tip: keep your gating/qualifying questions in this prompt for now (e.g. identity verification before
          discussing results). We can promote them to a structured feature later if you need per-question reporting.
        </Text>

        {tools.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Text strong>Available Tools (read-only)</Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              Actions the AI can perform automatically during a call. The AI decides when to use them based on the conversation.
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tools.map((tool) => (
                <div
                  key={tool.name}
                  style={{
                    border: '1px solid #eee',
                    borderRadius: 10,
                    padding: '12px 14px',
                    background: '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <FontAwesomeIcon icon={faWrench} style={{ color: COLORS.$mainBlack, fontSize: 13 }} />
                    <Text strong style={{ fontSize: 13 }}>{tool.label}</Text>
                    <Tag style={{ margin: 0, fontFamily: 'monospace', fontSize: 11 }}>{tool.name}</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#595959' }}>{tool.description}</div>
                  {tool.trigger && (
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                      <strong>When:</strong> {tool.trigger}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Call Handling */}
      <Card style={{ marginBottom: 24 }}>
        {sectionHeader(<FontAwesomeIcon icon={faPhone} />, 'Call Handling', 'Control what your AI receptionist can do')}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
          <div>
            <Text strong>Transfer to Human</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Allow callers to request a human when needed</Text>
          </div>
          <Switch
            checked={settings.callHandling?.transferToHuman}
            onChange={(v) => update('callHandling.transferToHuman', v)}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <Text strong>Transfer Phone Number</Text>
          <Input
            placeholder="(555) 123-4567"
            value={settings.callHandling?.transferPhoneNumber}
            onChange={(e) => update('callHandling.transferPhoneNumber', e.target.value)}
            disabled={!settings.callHandling?.transferToHuman}
            style={{ marginTop: 8, maxWidth: 320 }}
          />
        </div>
      </Card>

      {/* Action bar */}
      <div
        style={{
          position: 'sticky',
          bottom: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 12,
          marginTop: 8,
          padding: '16px 20px',
          background: '#fff',
          border: '1px solid #f0f0f0',
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
        }}
      >
        <Button
          size="large"
          icon={<FontAwesomeIcon icon={faCirclePlay} />}
          disabled={!configured}
          onClick={() => setCallModalVisible(true)}
          style={{ borderRadius: 8 }}
        >
          Test Call
        </Button>
        <Button
          type="primary"
          size="large"
          icon={<FontAwesomeIcon icon={faFloppyDisk} />}
          loading={saving}
          onClick={handleSave}
          style={{ borderRadius: 8, background: COLORS.$mainBlack, borderColor: COLORS.$mainBlack }}
        >
          Save Settings
        </Button>
      </div>

      <VoiceCallModal
        visible={callModalVisible}
        onClose={() => setCallModalVisible(false)}
        patient={null}
      />
    </div>
  );
};

export default VoiceSettings;
